import { isPlatformBrowser, isPlatformServer, NgClass, NgStyle } from '@angular/common';
import { Component, ElementRef, inject, input, NgZone, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { SLIDESHOW_SCHEMA } from 'src/types/builder-components/slideshow-schema';
import { Config, Slide } from '../../../../../types/builder-components/slideshow';
import { AppHomeTopSlideshowItemComponent } from '../home-top-slideshow-item/home-top-slideshow-item.component';

@Component({
  selector: 'app-home-top-slideshow',
  standalone: true,
  imports: [NgClass, NgStyle, AppHomeTopSlideshowItemComponent],
  templateUrl: './home-top-slideshow.component.html',
  styleUrls: ['./home-top-slideshow.styles.scss'],
})
export class AppHomeTopSlideshowComponent implements OnDestroy, OnInit {
  @ViewChild('homeSlideShow') homeSlideShow!: ElementRef<HTMLElement>;
  hide = input<boolean>();
  config = input<Config>();
  slides = input<Slide[]>();

  #platformId = inject(PLATFORM_ID);
  #zone = inject(NgZone);

  // TODO: We need to document the validation system used in the Page Component of Builder. We will use the documentation in the gitlab pages
  static inputsSchemaValidation = SLIDESHOW_SCHEMA; // Schema used to validate the Builder data model against the inputs of the component

  slider!: KeenSliderInstance;
  currentSlide: number = 0;
  slidesPerView: number = 1;
  dotHelper: Array<Number> = [];
  activeDot: number = 1;
  arrowVisibility: string = '';
  dotsVisibility: string = '';
  notSlider: string = '';

  get multiSlide() {
    return (this.slides()?.length ?? 0) > 1;
  }

  ngOnInit() {
    this.adjustVisibilityForBrowser('arrow');
    this.adjustVisibilityForBrowser('dots');

    this.#zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.createSlider();
      });
    });
  }

  ngOnDestroy(): void {
    if (this.slider) {
      this.slider.destroy();
    }
  }

  private createSlider() {
    if (isPlatformServer(this.#platformId)) {
      return;
    }
    if (isPlatformBrowser(this.#platformId)) {
      const slides = this.slides() ?? [];

      if (slides.length > 1) {
        this.slider = new KeenSlider(
          this.homeSlideShow.nativeElement,
          {
            loop: true,
            initial: this.currentSlide,
            defaultAnimation: {
              duration: 1000,
            },
            slides: {
              perView: () => {
                this.getActiveDot();
                return this.slidesPerView;
              },
            },
            slideChanged: (s) => {
              this.#zone.run(() => {
                this.currentSlide = s.track.details.rel;
              });
            },
          },
          [
            (slider) => {
              // Autoplay plugin. The slide is changed when mouse is not over the slider
              if (slides.length < 2) {
                return;
              }

              let timeout: any;
              let mouseOver = false;
              function clearNextTimeout() {
                clearTimeout(timeout);
              }
              function nextTimeout() {
                clearTimeout(timeout);
                if (mouseOver) return;
                timeout = setTimeout(() => {
                  slider.next();
                }, 5000);
              }
              slider.on('created', () => {
                slider.container.addEventListener('mouseover', () => {
                  mouseOver = true;
                  clearNextTimeout();
                });
                slider.container.addEventListener('mouseout', () => {
                  mouseOver = false;
                  nextTimeout();
                });
                nextTimeout();
              });
              slider.on('dragStarted', clearNextTimeout);
              slider.on('animationEnded', nextTimeout);
              slider.on('updated', nextTimeout);
            },
          ],
        );

        this.dotHelper = [...Array(this.slider.track.details.slides.length).keys()];
      }
    }
  }

  private getActiveDot() {
    this.dotHelper.map((dot, i) => {
      if (this.currentSlide + 1 <= this.slidesPerView * (i + 1) && this.currentSlide + 1 > this.slidesPerView * i) {
        this.activeDot = i;
      }
    });
  }

  adjustVisibilityForBrowser(type: 'arrow' | 'dots'): void {
    if (!this.multiSlide) {
      this.arrowVisibility = 'hidden';
      this.dotsVisibility = 'hidden';
      this.notSlider = 'not-slider';
      return;
    }

    const defaultConfig: Config = {
      desktop: { showNavigationArrows: true, showNavigationIndicators: false },
      tablet: { showNavigationArrows: false, showNavigationIndicators: true },
      mobile: { showNavigationArrows: false, showNavigationIndicators: true },
    };

    const slideshowConfig = this.config();
    const config: Config = {
      desktop: { ...defaultConfig.desktop, ...slideshowConfig?.desktop },
      tablet: { ...defaultConfig.tablet, ...slideshowConfig?.tablet },
      mobile: { ...defaultConfig.mobile, ...slideshowConfig?.mobile },
    };

    let className = [];

    if (type === 'arrow') {
      !config.desktop?.showNavigationArrows && className.push('hidden-md');
      !config.tablet?.showNavigationArrows && className.push('hidden-sm');
      !config.mobile?.showNavigationArrows && className.push('hidden-xs');

      this.arrowVisibility = className.join(' ');
    }

    if (type === 'dots') {
      !config.desktop?.showNavigationIndicators && className.push('hidden-md');
      !config.tablet?.showNavigationIndicators && className.push('hidden-sm');
      !config.mobile?.showNavigationIndicators && className.push('hidden-xs');

      this.dotsVisibility = className.join(' ');
    }
  }
}
